import React, { useState } from 'react';
import { Form, Container, Card, Alert, Spinner } from 'react-bootstrap';
import GradientButton from '../../components/GradientButton';
import { motion } from 'framer-motion';
import { FaCheckCircle } from 'react-icons/fa';
import api from '../../api/axios';

function AskExpert() {
  const [question, setQuestion] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await api.post('/expert/ask/', { question });
      
      setQuestion('');
      setSubmitted(true);
      
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error('❌ Błąd:', error);
      setError(error.response?.data?.error || 'Błąd podczas wysyłania pytania');
    } finally {
      setLoading(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div style={{ minHeight: '100vh', paddingTop: '2rem', paddingBottom: '4rem' }}>
      <Container>
        {/* HERO SECTION */}
        <motion.div {...fadeInUp} className="text-center mb-5">
          <h1 className="display-3 fw-bold text-white mb-3">
            Zapytaj eksperta
          </h1>
          <p className="lead text-white-50" style={{ fontSize: '1.1rem' }}>
            Masz pytanie dotyczące Twoich praw pracownika, urlopu lub wynagrodzenia? 
            Skontaktuj się z naszym ekspertem i otrzymaj poradę.
          </p>
        </motion.div>

        {/* FORMULARZ */}
        <div className="d-flex justify-content-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{ maxWidth: '800px', width: '100%' }}
          >
            <Card 
              className="border-0 shadow-lg"
              style={{ 
                borderRadius: '30px',
                overflow: 'hidden'
              }}
            >
              <Card.Header style={{
                background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)',
                border: 'none',
                padding: '2rem'
              }}>
                <h3 className="text-white mb-0 fw-bold">
                  Zadaj pytanie
                </h3>
              </Card.Header>
              <Card.Body className="p-4 p-md-5">
                {submitted && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Alert 
                      variant="success" 
                      className="border-0 mb-4"
                      style={{ 
                        borderRadius: '15px',
                        background: 'linear-gradient(135deg, rgba(67, 233, 123, 0.2) 0%, rgba(56, 249, 215, 0.2) 100%)'
                      }}
                    >
                      <FaCheckCircle className="me-2" />
                      Dziękujemy! Twoje pytanie zostało wysłane. Odpowiedź otrzymasz na adres email.
                    </Alert>
                  </motion.div>
                )}

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Alert 
                      variant="danger" 
                      className="border-0 mb-4"
                      style={{ 
                        borderRadius: '15px'
                      }}
                      onClose={() => setError('')}
                      dismissible
                    >
                      {error}
                    </Alert>
                  </motion.div>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium text-dark">
                      Twoje pytanie *
                    </Form.Label>
                    <Form.Control 
                      as="textarea"
                      rows={6}
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      placeholder="Opisz swoje pytanie lub wątpliwość w możliwie szczegółowy sposób..."
                      required
                      disabled={loading}
                      style={{
                        borderRadius: '15px',
                        border: '2px solid #e9ecef',
                        padding: '0.75rem 1rem',
                        fontSize: '1rem',
                        resize: 'none',
                        fontFamily: 'inherit'
                      }}
                    />
                    <small className="text-muted d-block mt-2">
                      Maksymalnie 5000 znaków ({question.length}/5000)
                    </small>
                  </Form.Group>

                  <div className="d-flex justify-content-center">
                    <GradientButton
                      type="submit"
                      gradient="blue"
                      disabled={loading || !question.trim()}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Wysyłanie...
                        </>
                      ) : (
                        'Wyślij pytanie do eksperta'
                      )}
                    </GradientButton>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </motion.div>
        </div>

        {/* INFO */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-5"
        >
          <div className="row g-3">
            <div className="col-md-4">
              <Card className="border-0 h-100" style={{
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                borderRadius: '20px'
              }}>
                <Card.Body className="text-center p-4">
                  <div style={{ fontSize: '2.5rem' }} className="mb-3">⏱️</div>
                  <h5 className="text-white fw-bold mb-2">Szybka odpowiedź</h5>
                  <p className="text-white-50 mb-0">
                    Staramy się odpowiedzieć w ciągu 48 godzin
                  </p>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-4">
              <Card className="border-0 h-100" style={{
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                borderRadius: '20px'
              }}>
                <Card.Body className="text-center p-4">
                  <div style={{ fontSize: '2.5rem' }} className="mb-3">👨‍⚖️</div>
                  <h5 className="text-white fw-bold mb-2">Ekspert</h5>
                  <p className="text-white-50 mb-0">
                    Porada od specjalisty prawa pracy
                  </p>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-4">
              <Card className="border-0 h-100" style={{
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                borderRadius: '20px'
              }}>
                <Card.Body className="text-center p-4">
                  <div style={{ fontSize: '2.5rem' }} className="mb-3">📧</div>
                  <h5 className="text-white fw-bold mb-2">Email</h5>
                  <p className="text-white-50 mb-0">
                    Odpowiedź na Twój adres email
                  </p>
                </Card.Body>
              </Card>
            </div>
          </div>
        </motion.div>
      </Container>
    </div>
  );
}

export default AskExpert;
